import {
  BarcodeFormat,
  BarcodeScanner,
} from '@capacitor-mlkit/barcode-scanning';
import { IonIcon, IonLabel, isPlatform } from '@ionic/react';
import { checkmarkCircle } from 'ionicons/icons';
import { ReactElement, ReactNode, useCallback, useState } from 'react';
import { FaQrcode } from 'react-icons/fa';
import i18n from '../../i18n';
import { logger } from '../../util/logger';
import { DownloadApp } from '../appstore/AppDownload';
import { LoadingButton } from '../button/loading-button';
import { useIsBarcodeScannerSupported } from './qrcode.hooks';

interface QrCodeScannerButtonProps {
  handleScanData: (data: string) => void;
  isValid: boolean;
  isDisabled?: boolean;
}

const QrCodeScannerButton = (props: QrCodeScannerButtonProps): ReactElement => {
  const { handleScanData, isValid, isDisabled } = props;

  const [isLoading, setIsLoading] = useState(false);
  const isSupported = useIsBarcodeScannerSupported();

  const launchScanner = useCallback(async () => {
    if (isSupported === true) {
      setIsLoading(true);
      try {
        const result = await BarcodeScanner.scan({
          formats: [BarcodeFormat.QrCode],
        });

        await handleScanData(result.barcodes[0].rawValue);
      } catch (error) {
        logger.error('Failed to scan QR code', { error });
      } finally {
        setIsLoading(false);
      }
    }
  }, [handleScanData, isSupported]);

  return (
    <>
      <LoadingButton
        loading={isLoading}
        className="ion-margin"
        buttonProps={{
          type: 'button',
          fill: 'outline',
          expand: 'block',
          disabled: !isSupported || isDisabled,
          onClick: launchScanner,
        }}
      >
        <FaQrcode className="padding-horizontal" />
        {i18n.t('common:machine_scan_qr')}
        {isValid && (
          <IonIcon
            className="padding-horizontal"
            icon={checkmarkCircle}
            color="success"
          />
        )}
      </LoadingButton>
      <QrCodeDeviceRequired />
    </>
  );
};

const QrCodeDeviceRequired = (): ReactNode => {
  const isSupported = useIsBarcodeScannerSupported();
  const canInstallApp = isPlatform('mobileweb') && !isPlatform('capacitor');

  if (isSupported) {
    return null;
  }

  return (
    <>
      <div className="ion-margin-vertical ion-text-center">
        <IonLabel color="danger">
          {i18n.t('machine_scan_qr_not_supported')}
        </IonLabel>
      </div>
      {canInstallApp === true && (
        <DownloadApp forceShow={true} hideAndroid={true} />
      )}
    </>
  );
};

export { QrCodeScannerButton };
